import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { ProductInfo } from "@redotech/redo-model/customer-event/customer-event-definition";
import { Fulfillment, LineItem, Order } from "@redotech/redo-model/order";
import { LineItemFulfillment } from "@redotech/redo-model/order-schema";
import { ProductInfo as ShopifyProductInfo } from "@redotech/shopify-storefront";
import { getDateString } from "@redotech/util/date-format";

export interface HiddenItemsTeamSettings {
  portalExcludedProductIds?: string[];
}

/**
 * Filters line items and fulfillments that should be hidden from the order. Filters out
 * line items that are redo products and line items that should be excluded based on the settings. Removes any fulfillments that have no line items.
 * Items that should be excluded based on settings: portalExcludedProductIds
 */
export const filterHiddenItemsFromOrder = <
  T extends Order,
  S extends HiddenItemsTeamSettings,
>(
  order: T,
  settings: S | undefined,
): T => {
  if (!order?.shopify) {
    return order;
  }

  const shouldExcludeLineItem = (item: LineItem) => {
    return (
      // filter out redo products
      item.vendor === "re:do" ||
      // filter out products that should be excluded based on settings
      settings?.portalExcludedProductIds?.some(
        (id) => id === item.product_id?.toString(),
      )
    );
  };

  const lineItemIdsToExclude = order.shopify.line_items
    .filter((item: LineItem) => shouldExcludeLineItem(item))
    .map((item: LineItem) => item.id?.toString());

  const lineItemFulfillmentIdsToExclude = order.shopify.fulfillments.flatMap(
    (fulfillment: Fulfillment) =>
      fulfillment.line_items
        .filter((item: LineItem) => shouldExcludeLineItem(item))
        .map((item: LineItem) => item.id?.toString()),
  );

  return {
    ...order,
    lineItems: order.lineItems?.filter(
      (item: LineItem) => !lineItemIdsToExclude.includes(item.id?.toString()),
    ),
    lineItemsFulfillment: order.lineItemsFulfillment?.filter(
      (item: LineItemFulfillment) =>
        !lineItemFulfillmentIdsToExclude.includes(
          item.shopify_line_item_id?.toString() ?? "",
        ),
    ),
    shopify: {
      ...order.shopify,
      line_items: order.shopify.line_items.filter(
        (item: LineItem) => !lineItemIdsToExclude.includes(item.id?.toString()),
      ),
      fulfillments: order.shopify.fulfillments
        .map((fulfillment: Fulfillment) => ({
          ...fulfillment,
          // filter out line items that should be excluded
          line_items: fulfillment.line_items.filter(
            (item: LineItem) =>
              !lineItemFulfillmentIdsToExclude.includes(item.id?.toString()),
          ),
        }))
        // filter out fulfillments that have no line items left
        .filter(
          (fulfillment: Fulfillment) => fulfillment.line_items.length > 0,
        ),
    },
  };
};

export const transformShopifyGraphQLProductInfoToRedoProductInfo = (
  products: ShopifyProductInfo[],
): ProductInfo[] => {
  return products.map((product: ShopifyProductInfo) => {
    const productInfo: ProductInfo = {
      productId: product.id,
      productTitle: product.title,
      price: {
        amount: Number(product.priceRange.minVariantPrice.amount),
        currencyCode: product.priceRange.minVariantPrice.currencyCode,
      },
      priceRange: product.priceRange,
      imageUrl: product.featuredImage?.url || product.images.nodes[0]?.url,
      handle: product.handle,
      url: product.onlineStoreUrl,
    };
    return productInfo;
  });
};

export const getProductUrlFromProductHandle = (
  storeUrl: string,
  handle: string | undefined,
) => {
  const formattedStoreUrl = storeUrl.endsWith("/") ? storeUrl : `${storeUrl}/`;
  return handle
    ? `https://${formattedStoreUrl}products/${handle}`
    : `https://${formattedStoreUrl}products`;
};

export const getProductUrl = (
  product: ProductInfo,
  storeUrl: string,
): string => {
  return product.url
    ? product.url
    : getProductUrlFromProductHandle(storeUrl, product.handle);
};

export const addProductUrlToProductInfo = (
  product: ProductInfo,
  storeUrl: string,
): ProductInfo => {
  const productUrl = getProductUrl(product, storeUrl);

  return { ...product, url: productUrl };
};

export const getOrderDate = (order: Order) => {
  const dateCreated = new Date(order.shopifyCreatedAt || order.createdAt);
  return getDateString(dateCreated);
};

export const formatProductInfoPriceRange = (product: ProductInfo) => {
  if (product.priceRange) {
    const minPrice = product.priceRange.minVariantPrice.amount;
    const maxPrice = product.priceRange.maxVariantPrice.amount;

    const formattedMin = moneyFormat({
      amount: minPrice,
      currency: product.priceRange.minVariantPrice.currencyCode as Currency,
    });
    const formattedMax = moneyFormat({
      amount: maxPrice,
      currency: product.priceRange.maxVariantPrice.currencyCode as Currency,
    });

    if (minPrice === maxPrice) {
      return formattedMin;
    }

    return `${formattedMin} - ${formattedMax}`;
  }
  return moneyFormat({
    amount: product.price.amount,
    currency: product.price.currencyCode as Currency,
  });
};
